import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Grid
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const { changePassword, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('error');

  useEffect(() => {
    clearError();
  }, [clearError]);

  // Show any context error (e.g., 401 from server) as toast
  useEffect(() => {
    if (error) {
      const msg = error;
      if (msg.toLowerCase().includes('invalid current password')) {
        setCurrentPasswordError('Current password is incorrect');
      }
      setToastSeverity('error');
      setToastMessage(msg);
      setToastOpen(true);
    }
  }, [error]);

  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentPassword(value);
    if (value && !validatePassword(value)) {
      setCurrentPasswordError('Password must be at least 6 characters');
    } else {
      setCurrentPasswordError('');
    }
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    if (value && !validatePassword(value)) {
      setNewPasswordError('Password must be at least 6 characters');
    } else {
      setNewPasswordError('');
    }
    
    // Also validate confirm password if it's already filled
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value && value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      if (!currentPassword) setCurrentPasswordError('Current password is required');
      if (!newPassword) setNewPasswordError('New password is required');
      if (!confirmPassword) setConfirmPasswordError('Confirm password is required');
      return;
    }

    if (!validatePassword(currentPassword)) {
      setCurrentPasswordError('Current password must be at least 6 characters');
      return;
    }

    if (!validatePassword(newPassword)) {
      setNewPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setNewPasswordError('New password must be different from current password');
      return;
    }

    try {
      await changePassword({
        currentPassword,
        newPassword,
        confirmPassword
      });
      setSuccessMessage('Password changed successfully! Redirecting to dashboard...');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: any) {
      // Show specific message for incorrect current password
      const msg = error?.response?.data?.message || error?.message || 'Password change failed';
      if (msg.toLowerCase().includes('invalid current password')) {
        setCurrentPasswordError('Current password is incorrect');
      }
      setToastSeverity('error');
      setToastMessage(msg);
      setToastOpen(true);
    }
  };

  const isFormValid = () => {
    return (
      currentPassword &&
      newPassword &&
      confirmPassword &&
      !currentPasswordError &&
      !newPasswordError &&
      !confirmPasswordError &&
      newPassword === confirmPassword &&
      currentPassword !== newPassword
    );
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" gutterBottom align="center">
            Change Password
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                required
                fullWidth
                name="currentPassword"
                label="Current Password"
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={handleCurrentPasswordChange}
                error={!!currentPasswordError}
                helperText={currentPasswordError}
                disabled={isLoading}
              />
              <TextField
                required
                fullWidth
                name="newPassword"
                label="New Password"
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={handleNewPasswordChange}
                error={!!newPasswordError}
                helperText={newPasswordError}
                disabled={isLoading}
              />
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                disabled={isLoading}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || !isFormValid()}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Change Password'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChangePassword;
